import { Logger, namespace, EventEmitter } from '../../../../../FrontendBundle/Resources/js/ofcs';
import ContextInterface from './contextInterface';

/**
 * @class ContextManager
 * @extends EventEmitter
 */
export default class ContextManager extends EventEmitter {
    /**
     * Triggered when the current context is switched
     *
     * @event switch
     * @param {ContextManager} contextManager Me
     * @param {Context} context The new context
     * @param {Context} previousContext The old context
     */

    /**
     * @type {Context}
     */
    context = null;

    /**
     * @type {Set}
     */
    contexts = new Set();

    /**
     * Initializes the context manager
     *
     * @param {Context} context
     */
    constructor(context) {
        super();

        if (context) this.addContext(context);
    }

    addContext(context) {
        Logger.log('contextManager.addContext()', context);
        ContextInterface.assert(context);

        this.contexts.add(context);

        if (!this.context) this.switchContext(context);
        this.trigger('add', this, context);
    }

    removeContext(context) {
        Logger.log('contextManager.removeContext()', context);

        this.contexts.delete(context);

        if (this.context === context) {
            this.switchToFirstContext();
        }
    }

    /**
     * Find the context for the given item
     *
     * @param {AbstractItem} item
     * @return {Context}
     */
    findContextByItem(item) {
        return [...this.contexts].find(context => context.getItem() === item);
    }

    /**
     * Find the nearest context for the given item
     *
     * @param {AbstractItem} item
     * @return {Context}
     */
    findNearestContextByItem(item) {
        if (!item) return null;

        return item
            .getAncestorsAndSelf()
            .reverse()
            .map(ancestor => this.findContextByItem(ancestor))
            .find(context => !!context);
    }

    /**
     * Check if given item is inside the current context
     *
     * @param {AbstractItem} item
     * @return {boolean}
     */
    isItemInCurrentContext(item) {
        return this.context === this.findNearestContextByItem(item);
    }

    /**
     * Switch the current context
     *
     * @param {Context} context
     * @return {boolean}
     */
    switchContext(context) {
        ContextInterface.assert(context);

        if (context === this.context) return false;
        if (!this.contexts.has(context)) return false;

        const oldContext = this.context;
        this.context = context;

        Logger.log('contextManager.switchContext()', context, oldContext);
        this.trigger('switch', this, context, oldContext);

        return true;
    }

    /**
     * Will set context to first registered
     * context as current context.
     */
    switchToFirstContext() {
        this.switchContext(this.getFirstContext());
    }

    /**
     * Will set context to first registered
     * context as current context.
     *
     * @return {Context}
     */
    getFirstContext() {
        if (!this.contexts.size) {
            throw new Error('Initial context requires at least one context item to be registered.');
        }

        return this.contexts.values().next().value;
    }

    /**
     * Will set context to first registered
     * context as current context.
     */
    switchToRootContext() {
        this.switchContext(this.getRootContext());
    }

    getRootContext() {
        if (!this.contexts.size) {
            throw new Error('Initial context requires at least one context item to be registered.');
        }

        const contexts = [...this.contexts];

        // eslint-disable-next-line arrow-body-style
        return contexts.find((context) => {
            return !contexts
                .filter(otherContext => otherContext !== context)
                .find(otherContext => context.getItem().isDescendantOf(otherContext.getItem()));
        });
    }

    /**
     * Return the current context
     *
     * @return {Context}
     */
    getCurrentContext() {
        return this.context;
    }

    /**
     * Return the item of the current context
     *
     * @return {AbstractItem}
     */
    getCurrentItem() {
        if (!this.context) return null;
        return this.context.getItem();
    }

    /**
     * Return the items of the current context
     *
     * @return {ItemCollection}
     */
    getCurrentItems() {
        return this.context.getItems();
    }

    /**
     * @return {Context[]}
     */
    getContexts() {
        return [...this.contexts];
    }
}

namespace('Ofcs.Editor.Context');
window.Ofcs.Editor.Context.ContextManager = ContextManager;
